import type { HandLandmark, HandDetectorStatus } from '../camera/hand-detector';
import type { GestureResult } from '../camera/gesture-detector';

const HAND_CONNECTIONS: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20],
  [0, 17],
];

const FINGER_COLORS: Record<string, string> = {
  thumb: '#a0ffc0',
  index: '#fca',
  middle: '#fb6',
  ring: '#ffd',
  pinky: '#c8f',
};

type StatusKind = 'pending' | 'ok' | 'warn' | 'err';

const GESTURE_LABEL: Record<string, string> = {
  fist: '出拳',
  scissors: '剪刀手',
  open_palm: '张手',
  unknown: '未识别',
};

export class GestureHint {
  private root: HTMLDivElement;
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private statusEl: HTMLDivElement;
  private gestureBadge: HTMLDivElement;
  private statDots: Record<string, HTMLSpanElement> = {};
  private statTexts: Record<string, HTMLSpanElement> = {};
  private actionEls: Record<string, HTMLSpanElement> = {};
  private lastGestureAt: Record<string, number> = { fist: 0, scissors: 0, open_palm: 0 };
  private expanded = false;
  private detailsEl: HTMLDivElement;
  private errLogEl: HTMLDivElement;
  private recentEvents: string[] = [];
  private lastHandAt = 0;

  constructor(parent: HTMLElement, video: HTMLVideoElement) {
    this.video = video;
    this.root = document.createElement('div');
    this.root.className = 'eye-hint';
    parent.appendChild(this.root);

    const header = document.createElement('div');
    header.className = 'eye-hint-header';
    header.innerHTML =
      '<span class="eye-hint-title">手势识别</span><button class="eye-hint-toggle" type="button" title="展开诊断">▾</button>';
    header.querySelector('.eye-hint-toggle')!.addEventListener('click', () =>
      this.toggleExpand()
    );
    this.root.appendChild(header);

    const videoWrap = document.createElement('div');
    videoWrap.className = 'eye-hint-video';
    this.root.appendChild(videoWrap);

    this.video.style.cssText =
      'display:block;width:100%;height:100%;object-fit:cover;transform:scaleX(-1);border-radius:6px;background:#0a1a0a;';
    videoWrap.appendChild(this.video);

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'eye-hint-canvas';
    this.canvas.width = 240;
    this.canvas.height = 180;
    videoWrap.appendChild(this.canvas);
    const c = this.canvas.getContext('2d');
    if (!c) throw new Error('Failed to get 2D context');
    this.ctx = c;

    this.gestureBadge = document.createElement('div');
    this.gestureBadge.className = 'eye-hint-gesture';
    this.gestureBadge.textContent = '— 未识别 —';
    this.root.appendChild(this.gestureBadge);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'eye-hint-status';
    const statKeys: { key: string; label: string }[] = [
      { key: 'camera', label: '摄像头' },
      { key: 'model', label: '模型' },
      { key: 'hand', label: '手部' },
    ];
    for (const s of statKeys) {
      const item = document.createElement('span');
      item.className = 'stat-item';
      item.innerHTML = `<span class="dot pending" data-stat="${s.key}"></span><span class="text" data-stat-text="${s.key}">${s.label}</span>`;
      this.statusEl.appendChild(item);
      this.statDots[s.key] = item.querySelector('.dot') as HTMLSpanElement;
      this.statTexts[s.key] = item.querySelector('.text') as HTMLSpanElement;
    }
    this.root.appendChild(this.statusEl);

    const row = document.createElement('div');
    row.className = 'eye-hint-actions';
    const actions = [
      { key: 'fist', label: '出拳' },
      { key: 'scissors', label: '剪刀' },
      { key: 'open_palm', label: '张手' },
    ];
    for (const a of actions) {
      const el = document.createElement('span');
      el.className = 'eye-hint-action';
      el.dataset.action = a.key;
      el.innerHTML = `<span class="lbl">${a.label}</span><span class="ts"></span>`;
      row.appendChild(el);
      this.actionEls[a.key] = el;
    }
    this.root.appendChild(row);

    this.detailsEl = document.createElement('div');
    this.detailsEl.className = 'eye-hint-details';
    this.detailsEl.style.display = 'none';
    this.detailsEl.innerHTML = `
      <div class="detail-row"><span>拇/食/中/无/小</span><span data-k="fingers">— / — / — / — / —</span></div>
      <div class="detail-row"><span>置信度</span><span data-k="conf">—</span></div>
      <div class="detail-row"><span>最近动作</span><span data-k="last">—</span></div>
    `;
    this.root.appendChild(this.detailsEl);

    this.errLogEl = document.createElement('div');
    this.errLogEl.className = 'eye-hint-errlog';
    this.root.appendChild(this.errLogEl);
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? 'flex' : 'none';
  }

  private toggleExpand(): void {
    this.expanded = !this.expanded;
    this.detailsEl.style.display = this.expanded ? 'flex' : 'none';
    this.root.classList.toggle('expanded', this.expanded);
    const btn = this.root.querySelector('.eye-hint-toggle') as HTMLButtonElement;
    if (btn) btn.textContent = this.expanded ? '▴' : '▾';
  }

  private setStatus(key: string, kind: StatusKind, text: string): void {
    const dot = this.statDots[key];
    const t = this.statTexts[key];
    if (dot) dot.className = `dot ${kind}`;
    if (t) t.textContent = text;
  }

  private logError(msg: string): void {
    const ts = new Date().toLocaleTimeString();
    this.recentEvents.unshift(`[${ts}] ${msg}`);
    this.recentEvents = this.recentEvents.slice(0, 4);
    this.errLogEl.innerHTML = this.recentEvents
      .map((e) => `<div class="err-item">${e}</div>`)
      .join('');
  }

  update(
    handLandmarks: HandLandmark[] | null,
    gestureResult: GestureResult,
    now: number,
    detectorStatus: HandDetectorStatus,
    videoState: { hasStream: boolean; readyState: number }
  ): void {
    const w = this.canvas.clientWidth || 240;
    const h = this.canvas.clientHeight || 180;
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (videoState.hasStream) {
      if (videoState.readyState >= 2) {
        this.setStatus('camera', 'ok', '摄像头 ✓');
      } else {
        this.setStatus('camera', 'warn', '摄像头加载中');
      }
    } else {
      this.setStatus('camera', 'err', '摄像头未就绪');
    }

    if (detectorStatus.modelLoaded) {
      this.setStatus('model', 'ok', '模型 ✓');
    } else if (detectorStatus.modelError) {
      this.setStatus('model', 'err', '模型加载失败');
      if (this.expanded) this.logError(`模型: ${detectorStatus.modelError.slice(0, 60)}`);
    } else {
      this.setStatus('model', 'pending', '模型加载中');
    }

    if (handLandmarks && handLandmarks.length >= 21) {
      this.setStatus('hand', 'ok', '手部 ✓');
      this.lastHandAt = now;
      this.drawHand(handLandmarks, gestureResult);
    } else {
      this.setStatus('hand', 'warn', '未检测到手');
    }

    const gestureLabel = GESTURE_LABEL[gestureResult.gesture] ?? '未识别';
    this.gestureBadge.textContent =
      gestureResult.gesture === 'unknown' ? '— 未识别 —' : `✓ ${gestureLabel}`;
    this.gestureBadge.className = `eye-hint-gesture ${gestureResult.gesture}`;

    if (gestureResult.gesture !== 'unknown') {
      this.lastGestureAt[gestureResult.gesture] = now;
    }
    for (const key in this.actionEls) {
      const lastTime = this.lastGestureAt[key] || 0;
      const isActive = now - lastTime < 350;
      this.actionEls[key].classList.toggle('flash', isActive);
      const tsEl = this.actionEls[key].querySelector('.ts') as HTMLSpanElement;
      if (lastTime > 0) {
        const ago = Math.max(0, Math.round((now - lastTime) / 1000));
        tsEl.textContent = `${ago}s`;
      } else {
        tsEl.textContent = '';
      }
    }

    if (this.expanded) {
      const f = gestureResult.fingerState;
      const fingersEl = this.detailsEl.querySelector('[data-k="fingers"]') as HTMLSpanElement;
      const confEl = this.detailsEl.querySelector('[data-k="conf"]') as HTMLSpanElement;
      const lastEl = this.detailsEl.querySelector('[data-k="last"]') as HTMLSpanElement;
      if (fingersEl) {
        fingersEl.textContent = `${f.thumb ? 1 : 0}/${f.index ? 1 : 0}/${f.middle ? 1 : 0}/${f.ring ? 1 : 0}/${f.pinky ? 1 : 0}`;
      }
      if (confEl) confEl.textContent = `${(gestureResult.confidence * 100).toFixed(0)}%`;
      const last: string[] = [];
      for (const k of ['fist', 'scissors', 'open_palm'] as const) {
        if (this.lastGestureAt[k]) {
          const ago = Math.round((now - this.lastGestureAt[k]) / 1000);
          last.push(`${GESTURE_LABEL[k]} ${ago}s`);
        }
      }
      if (lastEl) lastEl.textContent = last.length ? last.join(' · ') : '—';
    }
  }

  private drawHand(lm: HandLandmark[], gr: GestureResult): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const project = (p: HandLandmark) => ({
      x: (1 - p.x) * w,
      y: p.y * h,
    });

    this.ctx.strokeStyle = 'rgba(252, 200, 100, 0.6)';
    this.ctx.lineWidth = 2;
    for (const [a, b] of HAND_CONNECTIONS) {
      const pa = lm[a];
      const pb = lm[b];
      if (!pa || !pb) continue;
      const A = project(pa);
      const B = project(pb);
      this.ctx.beginPath();
      this.ctx.moveTo(A.x, A.y);
      this.ctx.lineTo(B.x, B.y);
      this.ctx.stroke();
    }

    const fingerKeys: Array<keyof typeof gr.fingerState> = ['thumb', 'index', 'middle', 'ring', 'pinky'];
    for (let i = 0; i < lm.length; i++) {
      const p = lm[i];
      if (!p) continue;
      const P = project(p);
      const fingerIdx = i <= 4 ? 0 : i <= 8 ? 1 : i <= 12 ? 2 : i <= 16 ? 3 : 4;
      const key = fingerKeys[fingerIdx];
      const isExt = gr.fingerState[key];
      this.ctx.fillStyle = isExt ? FINGER_COLORS[key] : '#6c8a7c';
      this.ctx.beginPath();
      this.ctx.arc(P.x, P.y, isExt ? 3.5 : 2, 0, Math.PI * 2);
      this.ctx.fill();
    }

    const tipIdx: Record<string, number> = { thumb: 4, index: 8, middle: 12, ring: 16, pinky: 20 };
    for (const key of fingerKeys) {
      const tip = lm[tipIdx[key]];
      if (!tip) continue;
      const P = project(tip);
      this.ctx.font = '10px "JetBrains Mono", monospace';
      this.ctx.fillStyle = gr.fingerState[key] ? FINGER_COLORS[key] : '#6c8a7c';
      this.ctx.fillText(key.slice(0, 1).toUpperCase(), P.x - 3, P.y - 8);
    }
  }
}
